import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { parseRouteId } from '../../utils/routeId';
import { detailNavState } from './detailNavigation';

/**
 * ลิงก์เก่าแบบ `?view=<id>` / `?edit=<id>` บนหน้ารายการ (สมัยเปิดโมดัล) → เด้งไปหน้าเต็ม
 *
 * ลิงก์พวกนี้อยู่ในการแจ้งเตือนเก่า · bookmark · คู่มือ · แชทของทีม จึงต้องยังเปิดได้
 *
 * ครอบ element ของ route หน้ารายการใน App.tsx (ไม่ใช่ hook ในหน้ารายการ) เพราะหน้ารายการส่วนใหญ่มี effect
 * "เขียนตัวกรองลง URL (replace)" ตอน mount — ถ้าหน้ารายการ mount ก่อน effect นั้นลบ `view=` ทิ้ง/แย่ง navigate
 * กับการเด้ง · แบบนี้หน้ารายการไม่ mount เลยเมื่อมีพารามิเตอร์เก่า
 *
 * - replace (ไม่ทิ้งช่อง history ของ URL เก่า — กด Back แล้วไม่เด้งกลับมาวน)
 * - พารามิเตอร์อื่นที่เหลือ (เช่น `company=` ตัวกรอง) ถูกเก็บเป็น `from` ให้ปุ่มกลับของหน้ารายละเอียด
 * - id ไม่ใช่จำนวนเต็มบวก = ตัดพารามิเตอร์ทิ้ง อยู่หน้ารายการเฉย ๆ (ไม่เปิด /x/NaN)
 *
 * ```tsx
 * <Route path="/master-data/companies" element={
 *   <LegacyDetailRedirect view={id => `/master-data/companies/${id}`} edit={id => `/master-data/companies/${id}/edit`}>
 *     <CompanyPage />
 *   </LegacyDetailRedirect>
 * } />
 * ```
 */
interface LegacyDetailRedirectProps {
  view?: (id: number) => string;
  edit?: (id: number) => string;
  children: React.ReactNode;
}

const LegacyDetailRedirect: React.FC<LegacyDetailRedirectProps> = ({ view, edit, children }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const editRaw = edit ? params.get('edit') : null;
  const viewRaw = view ? params.get('view') : null;
  if (editRaw === null && viewRaw === null) return <>{children}</>;

  params.delete('view');
  params.delete('edit');
  const restSearch = params.toString() ? `?${params.toString()}` : '';
  const id = parseRouteId(editRaw ?? viewRaw);
  if (id === null) {
    return <Navigate to={`${location.pathname}${restSearch}`} replace />;
  }
  const to = editRaw !== null ? edit!(id) : view!(id);
  return <Navigate to={to} replace state={detailNavState({ pathname: location.pathname, search: restSearch })} />;
};

export default LegacyDetailRedirect;
